import cn from "@/utils/cn"
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react"
import { ComponentPropsWithRef, useMemo, useState } from "react"

type EmailInputProps = Omit<ComponentPropsWithRef<"input">, "autoComplete"> & {
  isError?: boolean
  autoComplete?: "email" | "username"
}

export const EmailInput = ({
  autoComplete = "email",
  className,
  isError,
  ...otherProps
}: EmailInputProps) => (
  <input
    className={cn(
      "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 text-primary/90 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
      "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
      isError ? "ring-red-600 focus:ring-red-600" : "",
      className
    )}
    type="email"
    autoComplete={autoComplete}
    {...otherProps}
  />
)

type PasswordInputProps = {
  autoComplete?: "current-password" | "new-password"
  isError?: boolean
} & Omit<ComponentPropsWithRef<"input">, "autoComplete">

export const PasswordInput = ({
  isError,
  className,
  ...otherProps
}: PasswordInputProps) => (
  <input
    className={cn(
      "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
      "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
      isError ? "ring-red-600 focus:ring-red-600" : "",
      className
    )}
    type="password"
    {...otherProps}
  />
)

type SelectInputProps<T extends string | number> = {
  options: {
    value: T
    title: string
    tooltip?: string
    hidden?: boolean
    disabled?: boolean
  }[]
  placeholder?: string
} & Omit<ComponentPropsWithRef<"select">, "children">

export const SelectInput = <T extends string | number>({
  options = [],
  className,
  ...otherProps
}: SelectInputProps<T>) => (
  <select
    className={cn(
      "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
      "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
      className
    )}
    {...otherProps}
  >
    {options.map(({ value, title, tooltip, hidden, disabled }, i) => (
      <option
        key={`option-${i}`}
        value={value}
        title={tooltip}
        hidden={hidden}
        disabled={disabled}
      >
        {title}
      </option>
    ))}
  </select>
)

type SearchInputProps = ComponentPropsWithRef<"input">

export const SearchInput = ({ className, ...otherProps }: SearchInputProps) => (
  <div className="relative w-full">
    <span className="absolute inset-y-0 left-0 flex items-center pl-2.5">
      <span className="material-icons text-primary/60">search</span>
    </span>
    <input
      className={cn(
        "w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 placeholder:text-sm placeholder:text-primary/60 focus:border-slate-300 focus:ring-1 focus:ring-slate-300",
        "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
        className
      )}
      type="text"
      {...otherProps}
    />
  </div>
)

type TextInputProps = ComponentPropsWithRef<"input"> & {
  isError?: boolean
}

export const TextInput = ({
  className,
  isError,
  ...otherProps
}: TextInputProps) => (
  <input
    className={cn(
      "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
      "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
      isError ? "ring-red-600 focus:ring-red-600" : "",
      className
    )}
    type="text"
    {...otherProps}
  />
)

type TextareaInputProps = ComponentPropsWithRef<"textarea"> & {
  isError?: boolean
}

export const TextareaInput = ({
  className,
  isError,
  ...otherProps
}: TextareaInputProps) => (
  <textarea
    className={cn(
      "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
      "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
      isError ? "ring-red-600 focus:ring-red-600" : "",
      className
    )}
    {...otherProps}
  />
)

type PhoneInputProps = ComponentPropsWithRef<"input"> & {
  isError?: boolean
}

export const PhoneInput = ({
  className,
  isError,
  ...otherProps
}: PhoneInputProps) => (
  <input
    className={cn(
      "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
      "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
      isError ? "ring-red-600 focus:ring-red-600" : "",
      className
    )}
    type="text"
    {...otherProps}
  />
)

type DateInputProps = ComponentPropsWithRef<"input"> & {
  isError?: boolean
}

export const DateInput = ({
  className,
  isError,
  ...otherProps
}: DateInputProps) => (
  <input
    className={cn(
      "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
      "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
      isError ? "ring-red-600 focus:ring-red-600" : "",
      className
    )}
    type="date"
    {...otherProps}
  />
)

type CheckboxInputProps = Omit<ComponentPropsWithRef<"input">, "type">

export const CheckboxInput = ({ className, ...props }: CheckboxInputProps) => (
  <input
    className={`h-5 w-5 rounded-md text-primary focus:ring-0 ${className}`}
    type="checkbox"
    {...props}
  />
)

type ComboboxProps<T extends string> = {
  id: string
  options: { value: T; title: string; hidden?: boolean }[]
  value?: T
  placeholder?: string
  onSelect?: (value: T | null) => void
  className?: string
}

export const Combobox = <T extends string>({
  id,
  options = [],
  value,
  placeholder = "Search...",
  onSelect,
  className,
}: ComboboxProps<T>) => {
  const [query, setQuery] = useState("")

  const filteredOptions = useMemo(() => {
    return query === ""
      ? options
      : options.filter((option) =>
          option.title.toLowerCase().includes(query.toLowerCase())
        )
  }, [options, query])

  const nothingFound = filteredOptions.length === 0 && query !== ""

  return (
    <HeadlessCombobox name={id} value={value} onChange={onSelect}>
      <div className="relative">
        <div>
          <ComboboxInput
            className={cn(
              "block w-full rounded-lg border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-gray-300 placeholder:text-primary/60 focus:ring-2 focus:ring-inset focus:ring-blue-600",
              "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-primary/60",
              className
            )}
            displayValue={(value: T) =>
              options.find((option) => option.value === value)?.title || ""
            }
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
          />
          <ComboboxButton className="cursor-pointer absolute inset-y-0 right-0 flex items-center pr-2">
            <span className="material-icons-round text-primary/70">
              expand_more
            </span>
          </ComboboxButton>
        </div>
        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {nothingFound ? (
            <div className="relative cursor-default select-none px-4 py-2 text-primary">
              Nothing found.
            </div>
          ) : (
            filteredOptions.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option.value}
                hidden={option.hidden}
                className={({ focus }) =>
                  cn(
                    "relative cursor-pointer px-4 py-2",
                    focus ? "bg-blue-100" : "text-primary"
                  )
                }
              >
                {({ selected }) => (
                  <div className="flex items-center justify-between">
                    <span className="truncate">{option.title}</span>
                    {selected && (
                      <span className="material-icons-round text-blue-600">
                        check
                      </span>
                    )}
                  </div>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  )
}
